import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { clearOnsiteInterviews } from '~/actions/PermanentJobs';
import { Button, Spinner, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { Center, Row } from '~/commonComponents';
import { LeaveInterviewCreationPopup } from '~/commonComponents/popups/LeaveInterviewCreationPopup';
import { OverlappingJobPopup } from '~/commonComponents/popups/OverlappingJobPopup';
import { useCreateOnsiteInterview, useGetJobPostDetail } from '~/domain/PermanentJobs';
import { OverlappingJobError } from '~/domain/PermanentJobs/errors';
import { PostingType } from '~/enums/PostingType';
import { useAppSelector, useNavigationDetector, usePreventBackNavigation } from '~/hooks';
import { OnsiteInterviewPreviewer } from '../components';
import { TJobOverviewPageRoutesLocationState } from '../JobOverview';
import { TCreateOnsiteInterviewPageRoutes } from './Types';
import {
  fireInterviewRequestCancelClicked,
  fireInterviewRequestInviteSent,
  fireInterviewRequestPostInterviewsClicked,
} from '~/posthogs/events';

export const ReviewAndConfirmPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: jobPostingId } = useParams<TCreateOnsiteInterviewPageRoutes>();

  // Prevent browser back button navigation - redirect to postings list instead
  usePreventBackNavigation({ targetKickUrl: '/permanent-jobs/postings' });

  const { data, isLoading } = useGetJobPostDetail(jobPostingId);
  const { error, isError, isPending, isSuccess, mutate, reset } = useCreateOnsiteInterview();

  const interviews = useAppSelector((state) => {
    if (!jobPostingId) return [];
    return state.permanentJobs.onsiteInterviews[jobPostingId] ?? [];
  });

  const [showUnsavedChangesPopup, setShowUnsavedChangesPopup] = useState(false);

  // Ref breaks the cycle between onNavigationAttempt and the helpers it returns.
  const navHelpersRef = useRef<{
    allowNavigation: () => void;
    navigateToPending: () => void;
  }>({ allowNavigation: () => {}, navigateToPending: () => {} });

  const { allowNavigation, clearPendingNavigation, navigateToPending } = useNavigationDetector({
    when: interviews.length > 0,
    onNavigationAttempt: (targetLocation: { pathname?: string }) => {
      const path = targetLocation?.pathname ?? '';
      const isAddInterviews = /\/onsite-interviews\/create\/work-details$/.test(path);
      const isEditInterview = /\/onsite-interviews\/[^/]+\/edit$/.test(path);

      if (isAddInterviews || isEditInterview) {
        navHelpersRef.current.allowNavigation();
        navHelpersRef.current.navigateToPending();
        return;
      }

      setShowUnsavedChangesPopup(true);
    },
  });

  navHelpersRef.current = { allowNavigation, navigateToPending };

  useEffect(() => {
    if (isSuccess && jobPostingId) {
      allowNavigation();
      dispatch(clearOnsiteInterviews(jobPostingId));
      navigate(`/permanent-jobs/job-overview/${jobPostingId}/onsite-interviews`, {
        replace: true,
        state: {
          fromOnsiteInterviewCreation: true,
        } satisfies TJobOverviewPageRoutesLocationState,
      });
    }
  }, [allowNavigation, dispatch, isSuccess, jobPostingId, navigate]);

  const handleCancel = useCallback(() => {
    if (!jobPostingId) {
      return;
    }

    // Track the cancel clicked event
    fireInterviewRequestCancelClicked({
      postingId: jobPostingId,
    });

    allowNavigation();
    dispatch(clearOnsiteInterviews(jobPostingId));
    navigate(`/permanent-jobs/job-overview/${jobPostingId}/onsite-interviews`, { replace: true });
  }, [allowNavigation, dispatch, jobPostingId, navigate]);

  const handleSubmit = useCallback(() => {
    if (!jobPostingId) {
      return;
    }

    // Track the post interviews clicked event
    fireInterviewRequestPostInterviewsClicked({
      postingId: jobPostingId,
      interviewCount: interviews.length,
    });

    const totalCandidates = interviews.reduce((counter, curr) => {
      return counter + (curr.professionals?.length || 0);
    }, 0);

    mutate(
      {
        jobPostingId,
        officeId: data?.officeId,
        onsiteInterviews: interviews,
      },
      {
        onSuccess() {
          fireInterviewRequestInviteSent({
            postingId: jobPostingId,
            totalCandidatesSelected: totalCandidates,
            totalInterviews: interviews.length,
            totalInvitesSent: totalCandidates,
          });
        },
      },
    );
  }, [data?.officeId, interviews, jobPostingId, mutate]);

  const handleSubmitWithDuplicate = useCallback(() => {
    if (!jobPostingId) {
      return;
    }

    const totalCandidates = interviews.reduce((counter, curr) => {
      return counter + (curr.professionals?.length || 0);
    }, 0);

    mutate(
      {
        duplicate: true,
        jobPostingId,
        officeId: data?.officeId,
        onsiteInterviews: interviews,
      },
      {
        onSuccess() {
          fireInterviewRequestInviteSent({
            postingId: jobPostingId,
            totalCandidatesSelected: totalCandidates,
            totalInterviews: interviews.length,
            totalInvitesSent: totalCandidates,
          });
        },
      },
    );
  }, [data?.officeId, interviews, jobPostingId, mutate]);

  if (isLoading || !jobPostingId) {
    return (
      <Center marginBlock={48}>
        <Spinner centered variant="default" size="md" />
      </Center>
    );
  }

  return (
    <>
      <OnsiteInterviewPreviewer jobPostingId={jobPostingId} />
      <Typography italic color={colors.neutral[500]} style={{ textAlign: 'center', marginTop: 28 }}>
        Please Note: Onsite interviews will incur a separate fee once candidates are{' '}
        <strong>confirmed</strong> by your office.
      </Typography>
      <Row alignItems="center" justifyContent="center" gap={16} marginTop={66}>
        <Button variant="secondary" size="lg" disabled={isPending} onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          size="lg"
          disabled={interviews.length === 0}
          isLoading={isPending}
          onClick={handleSubmit}
        >
          {`Post ${interviews.length} ${interviews.length === 1 ? 'Interview' : 'Interviews'}`}
        </Button>
      </Row>

      <OverlappingJobPopup
        isOpen={isError && error && error instanceof OverlappingJobError}
        jobPostingType={PostingType.OnsiteInterview}
        onGoBack={reset}
        onConfirm={handleSubmitWithDuplicate}
      />

      <LeaveInterviewCreationPopup
        isOpen={showUnsavedChangesPopup}
        onDiscard={() => {
          setShowUnsavedChangesPopup(false);
          if (jobPostingId) {
            dispatch(clearOnsiteInterviews(jobPostingId));
          }
          allowNavigation();
          navigateToPending();
        }}
        onKeepEditing={() => {
          setShowUnsavedChangesPopup(false);
          clearPendingNavigation();
        }}
      />
    </>
  );
};
